import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Subject } from '@mui/icons-material';
import { Divider, Typography, Box } from '@mui/material';
import { Page, PageTitleWithBack, Card, Table, Pagination, Loader } from '../../../components';
import { IResponse, IETLJob, IETLJobHistory } from '../../../models';
import { getEtlJob, getEtlJobHistory } from '../../../fetch';
import { formatDateWithTimeAndSeconds } from '../../../helpers';

export const ETLJobsPage = () => {
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const { isLoading: isLoadingJob, data: eltJob } = useQuery<IETLJob, Error>(['etl-jobs'], () => getEtlJob());
  const { isLoading: isLoadingEtlJobs, data: eltJobsHistoryData } = useQuery<IResponse<IETLJobHistory[]>, Error>(
    ['etl-jobs-history', page, perPage],
    () =>
      getEtlJobHistory({
        page: page + 1,
        perPage,
        sortDirection: 'LogDate',
        sortBy: 'Date'
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const eltJobsHistory = useMemo(() => eltJobsHistoryData?.records || [], [eltJobsHistoryData]);
  const columns = useMemo(() => {
    return [
      {
        Header: 'Log Instance ID',
        accessor: 'etlLogInstanceId'
      },
      {
        Header: 'Log Date / Time',
        accessor: 'createDate',
        Cell: props => <>{formatDateWithTimeAndSeconds(props.cell.row.original.createDate)}</>
      },
      {
        Header: 'Log Message',
        accessor: 'logMessage'
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eltJobsHistory]);
  return (
    <Page title="ETL Jobs">
      <PageTitleWithBack showDivider={false} backText="Back to Admin home" backUrl="/admin" title="ETL Jobs" />
      {isLoadingJob && <Loader type="inline" position="centered" />}
      {!isLoadingJob && (
        <Card title="Details" icon={<Subject />}>
          <Box mb={1}>
            <Typography>{eltJob.jobDescription}</Typography>
          </Box>
          <Box mb={1}>
            <Divider />
          </Box>
          <Box mt={1} mb={3} display="flex" alignItems="center" justifyContent="center">
            <Box mr={1}>
              <Typography sx={{ fontWeight: 'bold' }} align="center">
                Last Transfer Start Time
              </Typography>
              <Typography align="center">{formatDateWithTimeAndSeconds(eltJob.lastStartTime)}</Typography>
            </Box>
            <Box ml={1}>
              <Typography sx={{ fontWeight: 'bold' }} align="center">
                Last Transfer End Time
              </Typography>
              <Typography align="center">{formatDateWithTimeAndSeconds(eltJob.lastEndTime)}</Typography>
            </Box>
          </Box>
          <Table columns={columns} data={eltJobsHistory} isLoading={isLoadingEtlJobs} hidePagination />
          {!isLoadingEtlJobs && <Pagination page={page} count={recordCount} rowsPerPage={perPage} setPage={setPage} setRowsPerPage={setRowsPerPage} />}
        </Card>
      )}
    </Page>
  );
};
